<template>
  <PainelLayout>
    <Breadcrumb :lista="[
      { 'name':'Painel', 'link' : '/' }]"
    />

    <div class="row">
      <div class="col-12">
        <h1>Assinatura</h1>
      </div>
    </div>
    <hr/>

    <div v-if="loading" class="d-flex justify-content-center p-3">
      <div class="spinner-border" style="width: 4rem; height: 4rem;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <template v-else-if="dados">
      <!-- Situação atual -->
      <div class="card mb-4">
        <div class="card-body d-flex flex-wrap justify-content-between gap-3">
          <div>
            <div class="text-muted small">Plano atual</div>
            <div class="h4 mb-1">{{ sub.plan_name }}</div>
            <span class="badge" :class="estado.classe">{{ estado.texto }}</span>
          </div>
          <div v-if="sub.state !== 'legacy'" class="d-flex gap-4">
            <div>
              <div class="text-muted small">Itens</div>
              <div class="fw-bold">{{ sub.usage.items }} / {{ sub.limits.items ?? '∞' }}</div>
            </div>
            <div>
              <div class="text-muted small">Contratos no mês</div>
              <div class="fw-bold">{{ sub.usage.contracts_per_month }} / {{ sub.limits.contracts_per_month ?? '∞' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="sub.state === 'legacy'" class="alert alert-info">
        Sua empresa é cliente desde antes dos planos e continua com acesso completo, sem cobrança pelo painel.
      </div>

      <template v-else>
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h2 class="h5 mb-0">Planos</h2>
          <div class="btn-group" role="group" aria-label="Período">
            <button v-for="p in periodos" :key="p.months" type="button" class="btn btn-sm"
              :class="meses === p.months ? 'btn-dark' : 'btn-outline-dark'" @click="meses = p.months">
              {{ nomePeriodo(p.months) }}
            </button>
          </div>
        </div>

        <div class="row g-3 mb-4" :class="{ 'justify-content-center': dados.plans.length === 1 }">
          <div v-for="plano in dados.plans" :key="plano.key" :class="dados.plans.length === 1 ? 'col-md-7 col-lg-5' : 'col-md-4'">
            <div class="card h-100" :class="{ 'border-primary': plano.key === sub.plan }">
              <div class="card-body d-flex flex-column">
                <h3 class="h5">{{ plano.name }}</h3>
                <div class="mb-2">
                  <span class="h4">{{ money(precoDe(plano).amount) }}</span>
                  <span class="text-muted small"> / {{ meses === 1 ? 'mês' : nomePeriodo(meses).toLowerCase() }}</span>
                  <div v-if="precoDe(plano).savings > 0" class="small text-success fw-semibold">
                    Economize {{ money(precoDe(plano).savings) }} pagando à vista
                  </div>
                </div>
                <ul class="small list-unstyled mb-3">
                  <li><i class="bi bi-check2 me-1"></i>{{ plano.limits.items === null ? 'Itens ilimitados' : 'Até ' + plano.limits.items + ' itens' }}</li>
                  <li><i class="bi bi-check2 me-1"></i>Vitrine online e central de pedidos</li>
                  <li v-for="f in plano.features" :key="f"><i class="bi bi-check2 me-1"></i>{{ recurso(f, plano) }}</li>
                </ul>
                <button class="btn mt-auto" :class="plano.key === sub.plan ? 'btn-primary' : 'btn-outline-primary'"
                  @click="abrirPagamento(plano)" :disabled="!dados.payments_enabled">
                  {{ plano.key === sub.plan ? 'Renovar' : 'Assinar' }}
                </button>
                <div class="text-center small text-muted mt-2">
                  <i class="bi bi-qr-code me-1"></i>Pix<template v-if="dados.card_enabled"> · <i class="bi bi-credit-card me-1"></i>Cartão de crédito ou débito</template>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p v-if="!dados.payments_enabled" class="text-muted small">Pagamento online ainda não configurado.</p>
        <p class="text-muted small">Pagamento único, sem renovação automática: Pix{{ dados.card_enabled ? ' ou cartão (crédito em até 12x)' : '' }}. O período é somado ao que você já tem.</p>

        <!-- Histórico -->
        <h2 class="h5 mt-4">Pagamentos</h2>
        <app-table :empty="historico.length === 0" empty-message="Nenhum pagamento ainda."
          :colunas="[{ label: 'Data' }, { label: 'Plano' }, { label: 'Período' }, { label: 'Forma' }, { label: 'Valor', align: 'right' }, { label: 'Status' }]">
          <app-trow v-for="p in historico" :key="p.id" :data="[
            { value: p.created_at, type: 'date' },
            { value: nomePlano(p.plan) },
            { value: p.months === 1 ? '1 mês' : nomePeriodo(p.months) },
            { value: formaPagamento(p) },
            { value: p.amount, type: 'currency', align: 'right' },
            { type: 'badge', label: statusPagamento(p.status).label, bg: statusPagamento(p.status).bg, text: statusPagamento(p.status).text, dot: statusPagamento(p.status).dot }
          ]" />
        </app-table>
      </template>
    </template>

    <!-- Modal de pagamento (Pix ou cartão) -->
    <div class="modal fade" id="modal_pagamento" tabindex="-1" aria-labelledby="tituloPagamento" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-scrollable">
        <div class="modal-content" v-if="escolha">
          <div class="modal-header">
            <h5 class="modal-title" id="tituloPagamento">
              {{ escolha.plano.name }} · {{ nomePeriodo(escolha.meses) }} — {{ money(escolha.valor) }}
            </h5>
            <button @click="fecharPagamento" type="button" class="btn-close" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <ul class="nav nav-pills nav-fill mb-3" role="tablist">
              <li class="nav-item">
                <button type="button" class="nav-link" :class="{ active: aba === 'pix' }" @click="trocarAba('pix')" :disabled="pagamentoTravado">
                  <i class="bi bi-qr-code me-1"></i>Pix
                </button>
              </li>
              <li class="nav-item">
                <button type="button" class="nav-link" :class="{ active: aba === 'cartao' }" @click="trocarAba('cartao')" :disabled="!dados.card_enabled || pagamentoTravado"
                  :title="dados.card_enabled ? '' : 'Pagamento com cartão ainda não configurado'">
                  <i class="bi bi-credit-card me-1"></i>Cartão
                </button>
              </li>
            </ul>

            <!-- Pix -->
            <div v-if="aba === 'pix'" class="text-center">
              <template v-if="!pix">
                <p class="text-muted">Gere o código e pague pelo app do seu banco. A confirmação aparece aqui na hora.</p>
                <button type="button" class="btn btn-primary" @click="gerarPix" :disabled="gerando">
                  <span v-if="gerando" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Gerar Pix
                </button>
              </template>
              <template v-else-if="pix.status === 'PENDING'">
                <img v-if="pix.pix_qr_code_base64" :src="'data:image/png;base64,' + pix.pix_qr_code_base64" alt="QR Code Pix" class="img-fluid mb-3" style="max-width: 240px;">
                <div class="input-group input-group-sm mb-2">
                  <input class="form-control" :value="pix.pix_qr_code" readonly aria-label="Pix copia e cola">
                  <button class="btn btn-outline-secondary" type="button" @click="copiarPix">Copiar</button>
                </div>
                <a v-if="pix.pix_ticket_url" :href="pix.pix_ticket_url" target="_blank" rel="noopener" class="small">Abrir instruções de pagamento</a>
                <p class="small text-muted mt-3 mb-0">
                  <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Aguardando pagamento… a confirmação aparece aqui automaticamente.
                </p>
              </template>
              <div v-else-if="pix.status === 'APPROVED'" class="py-3">
                <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem;"></i>
                <p class="mt-2 mb-0">Pagamento confirmado! Sua assinatura foi atualizada.</p>
              </div>
              <div v-else class="py-3">
                <i class="bi bi-x-circle text-danger" style="font-size: 3rem;"></i>
                <p class="mt-2">{{ pix.status === 'EXPIRED' ? 'O Pix expirou.' : 'O pagamento não foi aprovado.' }}</p>
                <button type="button" class="btn btn-outline-primary" @click="pix = null">Gerar outro Pix</button>
              </div>
            </div>

            <!-- Cartão -->
            <div v-else>
              <div v-if="cartao" class="text-center py-3">
                <template v-if="cartao.status === 'APPROVED'">
                  <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem;"></i>
                  <p class="mt-2 mb-0">Pagamento aprovado! Sua assinatura foi atualizada.</p>
                </template>
                <template v-else-if="cartao.status === 'PENDING'">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0">{{ cartao.message }}</p>
                </template>
                <template v-else>
                  <i class="bi bi-x-circle text-danger" style="font-size: 3rem;"></i>
                  <p class="mt-2">{{ cartao.message || 'O pagamento não foi aprovado.' }}</p>
                  <div class="d-flex flex-wrap justify-content-center gap-2">
                    <button type="button" class="btn btn-primary" @click="tentarOutroCartao">Tentar com outro cartão</button>
                    <button type="button" class="btn btn-outline-secondary" @click="trocarAba('pix')">Pagar com Pix</button>
                  </div>
                </template>
              </div>
              <div v-show="!cartao">
                <div v-if="carregandoCartao" class="d-flex justify-content-center py-4">
                  <div class="spinner-border" role="status"><span class="visually-hidden">Carregando formulário do cartão…</span></div>
                </div>
                <div id="cardPaymentBrick_container"></div>
                <p class="small text-muted mt-2 mb-0">
                  <i class="bi bi-shield-lock me-1"></i>Os dados do cartão vão direto para o Mercado Pago; o AlugUp não os armazena.
                  Débito: Elo e débito virtual Caixa.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PainelLayout>
</template>

<script>
import PainelLayout from '@/layouts/PainelLayout.vue';
import { onMounted, onBeforeUnmount, ref, computed, nextTick } from 'vue';
import { Modal } from "bootstrap";
import { billing } from '@/models/billing';
import Breadcrumb from '@/components/itens/breadcrumb.vue';
import AppTable from '@/components/AppTable.vue';
import AppTrow from '@/components/AppTrow.vue';
import { useSubscription } from '@/composables/useSubscription';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';
import { useAuth } from '@/services/auth';

// SDK do Mercado Pago (formulário seguro de cartão), carregado só quando o cliente escolhe Cartão
let sdkPromise = null;
function carregarSdkMercadoPago() {
  if (window.MercadoPago) return Promise.resolve();
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.onload = resolve;
      script.onerror = () => { sdkPromise = null; reject(new Error('Não foi possível carregar o formulário do cartão.')); };
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
}

const RECURSOS = {
  delivery_route: 'Roteiro de entregas',
  combos: 'Combos',
  freight: 'Frete automático',
};

const STATUS_PAGAMENTO = {
  PENDING:  { label: 'Aguardando', bg: '#fff4e5', text: '#a15c00', dot: '#f59e0b' },
  APPROVED: { label: 'Pago',       bg: '#e7f7ee', text: '#15803d', dot: '#22c55e' },
  REJECTED: { label: 'Recusado',   bg: '#fdecec', text: '#b91c1c', dot: '#ef4444' },
  EXPIRED:  { label: 'Expirado',   bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af' },
};

export default {
  name: "BillingView",
  components: { PainelLayout, Breadcrumb, AppTable, AppTrow },
  setup() {

    const toastStore = useToastStore();
    const model = billing();
    const { refresh } = useSubscription();
    const loading = ref(false);
    const gerando = ref(false);
    const dados = ref(null);
    const historico = ref([]);
    const meses = ref(1);
    const pix = ref(null);
    const cartao = ref(null);          // resultado do pagamento por cartão
    const escolha = ref(null);         // { plano, meses, valor } do modal aberto
    const aba = ref('pix');
    const carregandoCartao = ref(false);
    const auth = useAuth();
    let modal = null;
    let timer = null;
    let brick = null;

    // Depois de aprovado (ou com Pix aguardando), não deixa trocar de forma no meio
    const pagamentoTravado = computed(() =>
      cartao.value?.status === 'APPROVED' || cartao.value?.status === 'PENDING'
      || pix.value?.status === 'APPROVED'
    );

    const sub = computed(() => dados.value?.subscription);
    const periodos = computed(() => dados.value?.plans[0]?.prices ?? []);

    const estado = computed(() => {
      const s = sub.value;
      if (!s) return {};
      const data = v => v ? new Date(v).toLocaleDateString('pt-BR') : '';
      switch (s.state) {
        case 'trial':   return { classe: 'text-bg-warning', texto: `Teste grátis até ${data(s.trial_ends_at)}` };
        case 'active':  return { classe: 'text-bg-success', texto: `Ativa até ${data(s.paid_until)}` };
        case 'expired': return { classe: 'text-bg-danger',  texto: 'Vencida' };
        default:        return { classe: 'text-bg-secondary', texto: 'Sem cobrança' };
      }
    });

    async function carregar() {
      loading.value = true;
      try {
        dados.value = await model.getOne();
        if (dados.value.subscription.state !== 'legacy') {
          historico.value = await model.payments();
        }
      } catch (error) {
        toastStore.error('Erro ao carregar assinatura: ' + parseError(error));
      }
      loading.value = false;
    }

    function precoDe(plano) {
      return plano.prices.find(p => p.months === meses.value) ?? plano.prices[0];
    }

    function abrirPagamento(plano) {
      const preco = precoDe(plano);
      escolha.value = { plano, meses: preco.months, valor: Number(preco.amount) };
      pix.value = null;
      cartao.value = null;
      aba.value = 'pix';
      modal = modal ?? new Modal(document.getElementById('modal_pagamento'));
      modal.show();
    }

    async function gerarPix() {
      gerando.value = true;
      try {
        pix.value = await model.checkout(escolha.value.plano.key, escolha.value.meses);
        acompanhar(pix);
      } catch (error) {
        toastStore.error(parseError(error));
      }
      gerando.value = false;
    }

    async function trocarAba(nova) {
      // A aba do cartão sai do DOM ao trocar; o formulário é recriado ao voltar
      if (nova !== 'cartao') desmontarCartao();
      aba.value = nova;
      if (nova === 'cartao' && !cartao.value && !brick) {
        await nextTick();
        montarCartao();
      }
    }

    // Formulário oficial do Mercado Pago: gera o token do cartão no navegador
    async function montarCartao() {
      carregandoCartao.value = true;
      try {
        await carregarSdkMercadoPago();
        const mp = new window.MercadoPago(dados.value.mp_public_key, { locale: 'pt-BR' });
        brick = await mp.bricks().create('cardPayment', 'cardPaymentBrick_container', {
          initialization: {
            amount: escolha.value.valor,
            payer: { email: auth.user?.email || '' },
          },
          customization: {
            paymentMethods: { maxInstallments: 12 },
          },
          callbacks: {
            onReady: () => { carregandoCartao.value = false; },
            onSubmit: (formData, additionalData) => pagarCartao(formData, additionalData),
            onError: (erro) => {
              carregandoCartao.value = false;
              console.error('Card Payment Brick:', erro);
            },
          },
        });
      } catch (error) {
        carregandoCartao.value = false;
        toastStore.error(error.message || 'Não foi possível carregar o formulário do cartão.');
      }
    }

    function desmontarCartao() {
      if (brick) {
        try { brick.unmount(); } catch (e) { /* já desmontado */ }
      }
      brick = null;
    }

    async function pagarCartao(formData, additionalData) {
      try {
        const res = await model.payCard({
          plan: escolha.value.plano.key,
          months: escolha.value.meses,
          token: formData.token,
          payment_method_id: formData.payment_method_id,
          payment_type: additionalData?.paymentTypeId === 'debit_card' ? 'debit_card' : 'credit_card',
          installments: formData.installments || 1,
          payer_email: formData.payer?.email || auth.user?.email,
          identification: formData.payer?.identification || null,
        });
        cartao.value = res;
        desmontarCartao();
        if (res.status === 'APPROVED') {
          await refresh();
          carregar();
        } else if (res.status === 'PENDING') {
          acompanhar(cartao);
        }
      } catch (error) {
        cartao.value = { status: 'REJECTED', message: parseError(error) };
        desmontarCartao();
      }
    }

    function tentarOutroCartao() {
      cartao.value = null;
      trocarAba('cartao');
    }

    // Consulta o pagamento a cada 5s enquanto o modal está aberto (Pix aguardando ou cartão em análise)
    function acompanhar(alvo) {
      pararAcompanhamento();
      timer = setInterval(async () => {
        try {
          alvo.value = await model.getPayment(alvo.value.id);
          if (alvo.value.status !== 'PENDING') {
            pararAcompanhamento();
            if (alvo.value.status === 'APPROVED') {
              await refresh();
              carregar();
            }
          }
        } catch (e) {
          // tenta de novo no próximo ciclo
        }
      }, 5000);
    }

    function pararAcompanhamento() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function fecharPagamento() {
      pararAcompanhamento();
      desmontarCartao();
      if (modal) modal.hide();
      carregar();
    }

    async function copiarPix() {
      try {
        await navigator.clipboard.writeText(pix.value.pix_qr_code);
        toastStore.success('Código Pix copiado.');
      } catch (e) {
        toastStore.warning('Não foi possível copiar. Selecione o código e copie manualmente.');
      }
    }

    function recurso(f, plano) {
      if (f === 'contracts') {
        const lim = plano.limits.contracts_per_month;
        return lim === null ? 'Contratos digitais ilimitados' : `Contratos digitais (${lim}/mês)`;
      }
      return RECURSOS[f] ?? f;
    }

    function nomePeriodo(m) {
      if (m === 1) return 'Mensal';
      if (m === 12) return '1 ano';
      return m + ' meses';
    }

    function nomePlano(key) {
      return dados.value?.plans.find(p => p.key === key)?.name ?? key;
    }

    function formaPagamento(p) {
      if (p.method === 'CREDIT_CARD') return 'Crédito' + (p.installments > 1 ? ` ${p.installments}x` : '');
      if (p.method === 'DEBIT_CARD') return 'Débito';
      return 'Pix';
    }

    function statusPagamento(status) {
      return STATUS_PAGAMENTO[status] ?? STATUS_PAGAMENTO.EXPIRED;
    }

    function money(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    onMounted(carregar);
    onBeforeUnmount(() => {
      pararAcompanhamento();
      desmontarCartao();
    });

    return {
      loading, gerando, dados, sub, historico, meses, periodos, estado, pix, cartao, escolha, aba, carregandoCartao, pagamentoTravado,
      precoDe, abrirPagamento, gerarPix, trocarAba, tentarOutroCartao, fecharPagamento, copiarPix,
      recurso, nomePeriodo, nomePlano, formaPagamento, statusPagamento, money
    }
  }
}
</script>
